import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box, Typography, IconButton, TextField, InputAdornment, 
    Pagination, CircularProgress, 
    Grid, Tooltip, Chip, Avatar,
    Button, Divider, useMediaQuery,
    ToggleButtonGroup, ToggleButton
} from '@mui/material';
import { 
    Edit as EditIcon, 
    Delete as DeleteIcon, 
    Search as SearchIcon,
    Visibility as VisibilityIcon,
    Add as AddIcon,
    FilterList as FilterListIcon,
    Refresh as RefreshIcon,
    ViewList as ViewListIcon,
    ViewModule as ViewModuleIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import ClientDialog from './ClientDialog';
import InviteClientDialog from './invitations/InviteClientDialog';
import ImportClientsDialog from '../ImportClientsDialog';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { getCompanyClientUrlSegment } from '../../shared/clients/companyClientRouting';
import { setClientArchived } from '../../shared/clients/clientArchive';
import { listClients, deleteClient } from '../../api/companyRecords';
import { toJSDate } from '../../utils/timestampUtils';
import { useTheme, alpha } from '@mui/material/styles';
import { getStatusColor } from '../../utils/appointmentUtils';
import { stringAvatar } from '../../utils/avatarUtils';
import ClientGallery from './gallery/ClientGallery';
import ClientListView from './list/ClientListView';
import ClientSearchHeader from './search/ClientSearchHeader';
import './gallery/ClientGallery.css';
import './ClientManagement.css';
import '../../shared/management-layout/ManagementSplitLayout.css';
import { usePermissions } from '../../company-management/hooks/usePermissions';
import { PERMISSIONS } from '../../company-management/constants/permissions';

const ClientManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, companyData } = useAuth();
  const theme = useTheme();
  const { hasPermission } = usePermissions();
  
  // State declarations first
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { setNotification } = useNotification();
  const [isClientDialogOpen, setIsClientDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isInviteClientOpen, setIsInviteClientOpen] = useState(false);
  const [viewMode, setViewMode] = useState('gallery'); // 'gallery' or 'table'
  const [listMode, setListMode] = useState(() =>
    location.state?.listMode === 'archived' ? 'archived' : 'active'
  );
  
  // Add sorting state
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  
  const itemsPerPage = viewMode === 'gallery' ? 24 : 10;

  const [recentImports, setRecentImports] = useState([]);

  const exampleImports = [
    {
      filename: 'clients-march2024.csv',
      timestamp: new Date().toISOString(),
      status: 'success',
      successCount: 45
    },
    {
      filename: 'new-clients.csv',
      timestamp: new Date(Date.now() - 86400000).toISOString(),
      status: 'failed',
      successCount: 0,
      error: 'Invalid format'
    }
  ];

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  // Add state for hover effect on table rows
  const [hoveredRow, setHoveredRow] = useState(null);
  
  // Add state for refresh animation
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const handleViewProfile = (client) => {
    navigate(`/app/overview/clients/profile/${getCompanyClientUrlSegment(client)}`);
  };

  const handleNewNotification = useCallback((message, severity) => {
    setNotification({ open: true, message, severity });
  }, [setNotification]);

  // Define fetch functions
  const fetchClients = useCallback(async () => {
    if (!user || !companyData) return;

    setIsLoading(true);
    setError(null);
    try {
      const fetchedClients = await listClients({
        companyId: companyData.id,
        archived: listMode === 'archived',
      });
      const clientList = fetchedClients
        .filter((entry) => entry.id !== 'default' && !entry.isSystem && !entry.isDeleted)
        .map((data) => ({
          id: data.id,
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          email: data.email || '',
          phone: data.phone || '',
          archived: data.archived === true,
          archivedAt: data.archivedAt || null,
          createdAt: data.createdAt || null,
          updatedAt: data.updatedAt || null,
          name: data.name || `${data.firstName || ''} ${data.lastName || ''}`.trim(),
          searchName: data.searchName || `${data.firstName || ''} ${data.lastName || ''}`.toLowerCase().trim(),
          slug: data.slug || '',
          avatarUrl: data.avatarUrl || null,
          companyId: companyData.id
        }));

      setClients(clientList);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError(`Failed to fetch data: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [user, companyData, listMode]);
  
  // Enhanced fetch clients with animation - moved after fetchClients is defined
  const fetchClientsWithAnimation = useCallback(async () => {
    setIsRefreshing(true);
    await fetchClients();
    setTimeout(() => setIsRefreshing(false), 1000);
  }, [fetchClients]);

  // Use single useEffect for initial data fetch
  useEffect(() => {
    if (user && companyData) {
      fetchClients();
    }
  }, [user, companyData, fetchClients]);

  const handleAddClient = useCallback(() => {
    if (!hasPermission(PERMISSIONS.CLIENTS.CREATE)) return;
    setSelectedClient(null);
    setIsClientDialogOpen(true);
  }, [hasPermission]);

  const handleEditClient = useCallback((client) => {
    setSelectedClient(client);
    setIsClientDialogOpen(true);
  }, []);

  const handleSaveClient = useCallback(() => {
    setIsClientDialogOpen(false);
    fetchClients();
  }, [fetchClients]);

  const handleDeleteClient = useCallback(async (client) => {
    if (!user || !companyData) return;

    try {
      await deleteClient({ companyId: companyData.id, id: client.id });
      handleNewNotification('Client deleted successfully', 'success');
      fetchClients();
    } catch (error) {
      console.error("Error deleting client: ", error);
      handleNewNotification("Failed to delete client. Please try again.", "error");
    }
  }, [fetchClients, handleNewNotification, user, companyData]);

  const handleRestoreClient = useCallback(async (client) => {
    if (!user || !companyData || !hasPermission(PERMISSIONS.CLIENTS.EDIT)) return;

    try {
      await setClientArchived(companyData.id, client.id, false);
      handleNewNotification('Client restored successfully', 'success');
      fetchClients();
    } catch (error) {
      console.error('Error restoring client:', error);
      handleNewNotification('Failed to restore client. Please try again.', 'error');
    }
  }, [companyData, fetchClients, handleNewNotification, hasPermission, user]);

  const handleListModeChange = useCallback((mode) => {
    setListMode(mode);
    setCurrentPage(1);
    setSearchTerm('');
    setSortBy(mode === 'archived' ? 'archivedAt' : 'name');
    setSortDirection(mode === 'archived' ? 'desc' : 'asc');
  }, []);

  const handleImportClick = () => {
    setIsImportDialogOpen(true);
  };

  const handleExportClients = useCallback(() => {
    // Create CSV headers
    const headers = ['First Name', 'Last Name', 'Email', 'Phone'];
    
    const csvRows = [
      headers.join(','),
      ...clients.map(client => [
        client.firstName,
        client.lastName,
        client.email,
        client.phone,
      ].join(','))
    ];

    // Create blob and download
    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    // Create a URL for the blob
    const url = window.URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `clients_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.style.visibility = 'hidden';
    
    // Append to document, click, and cleanup
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, [clients]);

  const filteredClients = useMemo(() => {
    const filtered = clients.filter(client => {
      return !searchTerm ||
        client.searchName?.includes(searchTerm.toLowerCase()) ||
        client.email?.toLowerCase().includes(searchTerm.toLowerCase());
    });
    
    return [...filtered].sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'name':
          const aName = `${a.firstName} ${a.lastName}`.toLowerCase();
          const bName = `${b.firstName} ${b.lastName}`.toLowerCase();
          comparison = aName.localeCompare(bName);
          break;
        case 'createdAt':
          const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          comparison = aDate - bDate;
          break;
        case 'archivedAt':
          const aArchived = a.archivedAt?.toDate?.() || a.archivedAt || 0;
          const bArchived = b.archivedAt?.toDate?.() || b.archivedAt || 0;
          comparison = new Date(aArchived).getTime() - new Date(bArchived).getTime();
          break;
        default:
          comparison = 0;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [clients, searchTerm, sortBy, sortDirection]);

  const calculateClientStats = () => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    
    const activeClients = clients.filter(client => {
      // Define what makes a client "active" - for example, has appointments in last 3 months
      return true; // Replace with your logic
    }).length;

    const newClientsThisMonth = clients.filter(client => {
      const createdAt = toJSDate(client.createdAt);
      return createdAt && createdAt >= startOfMonth;
    }).length;

    return { activeClients, newClientsThisMonth };
  };

  const { activeClients, newClientsThisMonth } = calculateClientStats();

  // Calculate total pages
  useEffect(() => {
    setTotalPages(Math.ceil(filteredClients.length / itemsPerPage));
  }, [filteredClients, itemsPerPage]);

  // Handle view mode change
  const handleViewModeChange = (newMode) => {
    setViewMode(newMode);
    setCurrentPage(1); // Reset to first page when changing view mode
  };

  // Get paginated clients
  const paginatedClients = useMemo(() => {
    return filteredClients.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    );
  }, [filteredClients, currentPage, itemsPerPage]);

  // CSS variables for theming (similar to AccountSettings)
  const cssVars = useMemo(() => {
    const isDark = theme.palette.mode === 'dark';
    const bg = isDark ? '#0b1220' : '#f6f8fc';
    const surface = isDark ? '#0f172a' : '#ffffff';
    const surface2 = isDark ? 'rgba(15, 23, 42, 0.72)' : 'rgba(255, 255, 255, 0.7)';
    const text = isDark ? '#e5e7eb' : '#0f172a';
    const muted = isDark ? '#94a3b8' : '#64748b';
    const border = isDark ? 'rgba(148, 163, 184, 0.22)' : 'rgba(15, 23, 42, 0.12)';
    const shadow = isDark ? '0 10px 30px rgba(0,0,0,0.35)' : '0 10px 30px rgba(2, 6, 23, 0.08)';
    const shimmerA = isDark ? 'rgba(226, 232, 240, 0.08)' : 'rgba(15, 23, 42, 0.06)';
    const shimmerB = isDark ? 'rgba(226, 232, 240, 0.14)' : 'rgba(15, 23, 42, 0.12)';

    return {
      '--as-bg': bg,
      '--as-surface': surface,
      '--as-surface-2': surface2,
      '--as-text': text,
      '--as-muted': muted,
      '--as-border': border,
      '--as-shadow': shadow,
      '--as-primary': '#488aec',
      '--as-shimmer-a': shimmerA,
      '--as-shimmer-b': shimmerB
    };
  }, [theme.palette.mode]);

  const isDark = theme.palette.mode === 'dark';
  const darkSurface = '#0f172a';
  const darkBorder = 'rgba(148, 163, 184, 0.22)';
  const darkShadow = '0 10px 30px rgba(0,0,0,0.35)';

  if (!user || !companyData) {
    return <Typography>Loading user and company data...</Typography>;
  }

  return (
    <Box sx={{ 
      flexGrow: 1,
      width: '100%',
      minWidth: 0,
      boxSizing: 'border-box',
      p: 0,
      backgroundColor: 'transparent',
      ...cssVars
    }}>
      <div className="management-split">
        <aside className="management-split__filters">
          <ClientSearchHeader
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            onAddClient={handleAddClient}
            canCreateClient={hasPermission(PERMISSIONS.CLIENTS.CREATE)}
            onRefresh={fetchClientsWithAnimation}
            onInviteClient={() => setIsInviteClientOpen(true)}
            isRefreshing={isRefreshing}
            isLoading={isLoading}
            viewMode={viewMode}
            onViewModeChange={handleViewModeChange}
            listMode={listMode}
            onListModeChange={handleListModeChange}
            // Stats props
            totalClients={clients.length}
            activeClients={activeClients}
            newClientsThisMonth={newClientsThisMonth}
            onImportClick={handleImportClick}
            onExportClick={handleExportClients}
            // Extra action: Invite client
            extraActions={[
              {
                key: 'invite-client',
                label: 'Invite Client',
                icon: <AddIcon />,
                onClick: () => setIsInviteClientOpen(true),
                disabled: !hasPermission(PERMISSIONS.CLIENTS.CREATE),
              },
            ]}
          />
        </aside>

        <div className="management-split__content">
          {error ? (
            <div className="client-management-error">
              <p>{error}</p>
              <button type="button" className="clientSearchHeaderBtn secondary" onClick={fetchClients}>
                Try Again
              </button>
            </div>
          ) : viewMode === 'gallery' ? (
            <ClientGallery
              clients={paginatedClients}
              onViewProfile={handleViewProfile}
              onAddClient={listMode === 'active' ? handleAddClient : null}
              onRestoreClient={handleRestoreClient}
              listMode={listMode}
              isLoading={isLoading}
              emptyStateMessage={
                searchTerm
                  ? 'Try adjusting your search'
                  : listMode === 'archived'
                    ? 'No archived clients'
                    : 'Add your first client to get started'
              }
            />
          ) : (
            <ClientListView
              clients={paginatedClients}
              isLoading={isLoading}
              onViewProfile={handleViewProfile}
              onEdit={handleEditClient}
              onDelete={handleDeleteClient}
              onRestoreClient={handleRestoreClient}
              listMode={listMode}
              sortBy={sortBy}
              setSortBy={setSortBy}
              sortDirection={sortDirection}
              setSortDirection={setSortDirection}
              formatDate={(date) => {
                if (!date) return listMode === 'archived' ? 'Unknown' : 'Recently added';
                const dateObj = toJSDate(date);
                return dateObj ? format(dateObj, 'MMM d, yyyy') : (listMode === 'archived' ? 'Unknown' : 'Recently added');
              }}
            />
          )}

          {/* Pagination and Results Summary — only when more than one page */}
          {totalPages > 1 ? (
          <Box
            className={`client-management-pagination ${isDark ? 'is-dark' : ''}`}
            sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'center', sm: 'center' },
            mt: 3,
            p: 2,
            borderRadius: 2,
            border: `1px solid ${isDark ? darkBorder : alpha(theme.palette.primary.main, 0.2)}`,
            boxShadow: isDark ? '0 2px 10px rgba(0,0,0,0.2)' : '0 2px 10px rgba(0,0,0,0.05)',
            bgcolor: isDark ? darkSurface : alpha(theme.palette.background.paper, 0.8),
            backdropFilter: 'blur(8px)'
          }}>
            <Typography 
              variant="body2" 
              color="text.secondary"
              className="client-management-summary"
              sx={{ mb: { xs: 2, sm: 0 } }}
            >
              Showing {Math.min(filteredClients.length, (currentPage - 1) * itemsPerPage + 1)} - {Math.min(filteredClients.length, currentPage * itemsPerPage)} of {filteredClients.length} clients
            </Typography>
            
            <Pagination 
              count={totalPages} 
              page={currentPage} 
              onChange={(event, value) => setCurrentPage(value)} 
              color="primary"
              size="large"
              showFirstButton
              showLastButton
              sx={{
                '& .MuiPaginationItem-root': {
                  borderRadius: 1,
                  '&.Mui-selected': {
                    fontWeight: 'bold',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                  }
                }
              }}
            />
          </Box>
          ) : null}
        </div>
      </div>

      {/* Keep all existing dialogs and snackbar */}
      {/* Other Dialogs */}
      <ClientDialog 
        open={isClientDialogOpen}
        onClose={() => setIsClientDialogOpen(false)}
        client={selectedClient}
        existingClients={clients}
        onSave={handleSaveClient}
      />

      <ImportClientsDialog
        open={isImportDialogOpen}
        onClose={() => setIsImportDialogOpen(false)}
        onImport={fetchClients}
      />

      <InviteClientDialog
        open={isInviteClientOpen}
        onClose={() => setIsInviteClientOpen(false)}
        companyData={companyData}
        onInvited={() => setNotification({ open: true, message: 'Client invite sent', severity: 'success' })}
      />
    </Box>
  );
};

export default ClientManagement;
